import type { IUseCellRowDataRefresh, IUseCellRowDataRefreshParams } from 'components/AgGrid/types';
import { updateRows } from 'components/AgGrid/utils';
import type { TUseRequestUpdaterProps } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import type { UerWorkOrderPriceItemsQuery, WorkOrderPriceItemFilterInput } from 'gql/graphql';
import { pickTrue } from 'utils';

import * as R from 'ramda';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { uerWorkOrderPriceItemsGql } from '../../workOrderQueries';

const requestId = 'uerWorkOrderPriceItemsRefreshData';

type TIsItemType = {
    isSMRItem: { eq: boolean };
    isOtherItem: { eq: boolean };
    isRootObject: { eq: boolean };
};
export const useWorkOrderRowDataRefresh = (props: IUseCellRowDataRefreshParams): IUseCellRowDataRefresh => {
    const { api, requestParams } = props;
    const dispatch = useDispatch();

    const dependenciesUpdater = useCallback(
        (depsVars: TUseRequestUpdaterProps<UerWorkOrderPriceItemsQuery>) => {
            updateRows(depsVars.payload.uerWorkOrderPriceItems?.items || [], api);
        },
        [api],
    );

    const [, , , uerWorkOrderPriceItemsRequest] = useRequest({
        query: uerWorkOrderPriceItemsGql,
        withEffect: false,
        requestId,
        dependenciesUpdater,
    });

    const reloadHandler = useCallback(
        (reloadRowsIds?: number[] | null) => {
            if (!reloadRowsIds?.length) {
                return;
            }

            const isSMRItem = requestParams?.isSMRItem ? { eq: true } : undefined;
            const isOtherItem = requestParams?.isOtherItem ? { eq: true } : undefined;
            const isRootObject = requestParams?.isRootObject ? { eq: true } : undefined;

            const isItemTypeWhere = R.map(
                ([key, value]) => ({ [key]: value }),
                R.toPairs(pickTrue<TIsItemType, Partial<TIsItemType>>({ isSMRItem, isOtherItem, isRootObject })),
            );

            const where: WorkOrderPriceItemFilterInput = {
                itemId: { in: reloadRowsIds },
                or: isItemTypeWhere,
            };

            dispatch(
                uerWorkOrderPriceItemsRequest({
                    workOrderId: requestParams?.workOrderId,
                    constructionTypeId: requestParams?.constructionTypeId,
                    where,
                }),
            );
        },
        [dispatch, requestParams, uerWorkOrderPriceItemsRequest],
    );

    return { reloadHandler };
};
